import React, { useEffect, useRef } from 'react';

import { View } from 'react-native';

import { useTranslation } from 'react-i18next';

import { CloseIcon } from 'assets/images';
import AirPlayIcon from 'assets/images/svgs/Player/ico_airplay.svg';
import ExitFullscreenIcon from 'assets/images/svgs/Player/ico_fullscreen_reverse.svg';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import { PLAYER_EXIT_FULL_SCREEN_ICON } from 'config/testID.ts';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { COLOR } from 'utils/colors.ts';
import { isPhone, isTablet, isWeb } from 'utils/responsiveness.ts';

import AirPlayButton from 'components/AirPlayButton';
import SvgButton from 'components/CustomButtons/SvgButton';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    onMinimizeEmbeddedPlayer?: () => void;
    onCloseModal?: () => void;
    onClose?: () => void;
    embedded?: boolean;
    fullscreen?: boolean;
};

const {
    ACCESSIBILITY: {
        PLAYER: { ACCESSIBILITY_PLAYER_CLOSE, ACCESSIBILITY_PLAYER_MINIMIZE },
    },
    AIRPLAY_PLAYER_CONNECTED_TO_RECEIVER,
} = LANGUAGE_KEYS;

function PlayerOverlayAirPlay({
    onMinimizeEmbeddedPlayer,
    onClose,
    embedded,
    fullscreen,
}: Props) {
    const { t } = useTranslation();
    const closeRef = useRef(null);

    const isEmbeddedPhonePlayer = embedded && !fullscreen && isPhone;
    const isEmbeddedTabletPlayer = embedded && !fullscreen && isTablet;

    useEffect(() => {
        setAccessibilityFocusTo(closeRef);
    }, []);

    return (
        <View style={styles.root} pointerEvents="box-none" accessible={false}>
            <AirPlayIcon
                accessible={false}
                fill={COLOR.NEUTRAL}
                style={styles.airPlayIcon}
            />
            <Text
                category={
                    fullscreen ? ETextCategory.title3 : ETextCategory.body1
                }
                color={ETextColor.neutral}
                {...(isWeb && { tabIndex: 0 })}
                center
            >
                {t(AIRPLAY_PLAYER_CONNECTED_TO_RECEIVER)}
            </Text>
            <View
                style={[
                    styles.buttons,
                    isEmbeddedTabletPlayer && { top: 0, right: 54 },
                    isEmbeddedPhonePlayer && { top: 0, right: 17 },
                ]}
                accessible={false}
            >
                {!isEmbeddedTabletPlayer && !isEmbeddedPhonePlayer && (
                    <AirPlayButton />
                )}
                {fullscreen && embedded && (
                    <SvgButton
                        accessible
                        accessibilityRole="button"
                        accessibilityLabel={t(ACCESSIBILITY_PLAYER_MINIMIZE)}
                        Svg={ExitFullscreenIcon}
                        style={{ marginLeft: 8 }}
                        onPress={onMinimizeEmbeddedPlayer}
                        testID={PLAYER_EXIT_FULL_SCREEN_ICON}
                    />
                )}
                {!embedded && (
                    <SvgButton
                        ref={closeRef}
                        accessible
                        accessibilityLabel={t(ACCESSIBILITY_PLAYER_CLOSE)}
                        accessibilityRole="button"
                        Svg={CloseIcon}
                        style={[styles.closeButton, { marginLeft: 8 }]}
                        svgStyle={styles.closeSvg}
                        onPress={onClose}
                    />
                )}
            </View>
        </View>
    );
}

export default PlayerOverlayAirPlay;
